import { Component } from '@theme/component';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/*
 * Videos pegados en una descripción que arrancan recién al acercarse a la pantalla.
 *
 * `snippets/text.liquid` les saca el atributo `autoplay` y lo cambia por `data-autoplay-deferred`,
 * porque en Chromium `autoplay` le gana a `preload="none"` y el video se descargaba apenas se
 * parseaba el HTML. Medido en la ficha de huellitas con 4G lenta: la guía de talles (353 KB) se
 * pedía a los 2,0 s, sin scrollear. Sin `autoplay`, `preload="none"` vuelve a valer y el póster se
 * ve igual; este código recupera el arranque automático cuando el video queda a 300px de verse.
 *
 * Tres detalles que no son decorativos:
 *
 * - `muted` y `playsInline` se fijan como PROPIEDAD antes de `play()`. Sin `muted`, el navegador
 *   niega la reproducción automática; sin `playsInline`, iOS abre el video a pantalla completa.
 * - El `root` sale de `getIntersectionRoot()`: en escritorio el que scrollea es `.page-wrapper`, y
 *   con el viewport como raíz el `rootMargin` se perdería (ver `docs/css.md`). Al cruzar ese corte
 *   el observador se recrea, igual que en `assets/product-recommendations.js`.
 * - Se vuelve a barrer el documento en `DOMContentLoaded`: `assets/product-story.js` reordena la
 *   descripción después de que este componente conectó. Hoy mueve los nodos (conservan su
 *   identidad y el observador sigue valiendo), pero si alguna vez reconstruyera uno, el barrido lo
 *   vuelve a encontrar. El `WeakSet` evita observar dos veces el mismo video.
 */

/** @type {WeakSet<HTMLVideoElement>} */
const knownVideos = new WeakSet();

/** @type {Set<HTMLVideoElement>} */
const pendingVideos = new Set();

/** @type {IntersectionObserver | null} */
let videoObserver = null;

/** @param {HTMLVideoElement} video */
function startVideo(video) {
  pendingVideos.delete(video);
  video.muted = true;
  video.playsInline = true;
  const attempt = video.play();
  // Un navegador que igual niega la reproducción deja el póster a la vista: no es un error.
  if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
}

function createVideoObserver() {
  if (videoObserver) videoObserver.disconnect();

  videoObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (videoObserver) videoObserver.unobserve(video);
        startVideo(video);
      }
    },
    { root: getIntersectionRoot(), rootMargin: '300px 0px' }
  );

  for (const video of pendingVideos) {
    if (video.isConnected) videoObserver.observe(video);
    else pendingVideos.delete(video);
  }
}

/** @param {ParentNode} root */
function watchDeferredVideos(root) {
  for (const video of root.querySelectorAll('video[data-autoplay-deferred]')) {
    const element = /** @type {HTMLVideoElement} */ (video);
    if (knownVideos.has(element)) continue;
    knownVideos.add(element);
    pendingVideos.add(element);
    if (videoObserver) videoObserver.observe(element);
    else createVideoObserver();
  }
}

scrollContainerMediaQuery.addEventListener('change', () => {
  if (videoObserver) createVideoObserver();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => watchDeferredVideos(document), { once: true });
}

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    watchDeferredVideos(this);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
